import type { MoonPhaseId } from '@yadviga/shared'

interface Props {
  phase: MoonPhaseId
  className?: string
}

/**
 * 8-state moon icon as an SVG so it scales cleanly and inherits currentColor.
 * The disc is a full circle (rim) + a clipped illuminated portion. Phases
 * (new → waxing crescent → first quarter → waxing gibbous → full → ...)
 * are produced by varying the illuminated path.
 *
 * Avoids emoji ("🌒" etc) per project standard — keeps visual consistency
 * with the lucide-react icons used elsewhere.
 */
export function MoonPhaseIcon({ phase, className }: Props) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <circle cx="12" cy="12" r="10" fill="currentColor" fillOpacity="0.12" stroke="currentColor" strokeWidth="1.2" />
      <path d={ILLUMINATED_PATH[phase]} fill="currentColor" />
    </svg>
  )
}

// Each path describes the *illuminated* portion of the disc inside the
// circle. They're composed from arcs of the same circle (cx=12, cy=12,
// r=10) so they always stay inside the rim.
const ILLUMINATED_PATH: Record<MoonPhaseId, string> = {
  // No illumination at all.
  new: '',
  // Right-side thin crescent: outer right arc + inner small ellipse arc.
  waxing_crescent: 'M12 2 A10 10 0 0 1 12 22 A6 10 0 0 0 12 2 Z',
  // Right half illuminated.
  first_quarter: 'M12 2 A10 10 0 0 1 12 22 L12 2 Z',
  // Right + a bit of the left side.
  waxing_gibbous: 'M12 2 A10 10 0 0 1 12 22 A6 10 0 0 1 12 2 Z',
  // Whole disc.
  full: 'M12 2 A10 10 0 1 1 12 22 A10 10 0 1 1 12 2 Z',
  // Left + a bit of the right side.
  waning_gibbous: 'M12 2 A10 10 0 0 0 12 22 A6 10 0 0 0 12 2 Z',
  // Left half illuminated.
  last_quarter: 'M12 2 A10 10 0 0 0 12 22 L12 2 Z',
  // Left-side thin crescent.
  waning_crescent: 'M12 2 A10 10 0 0 0 12 22 A6 10 0 0 1 12 2 Z',
}
